import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, map, Observable, ReplaySubject, Subject, switchMap } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { completeSubject, promiseWithCatch } from 'src/app/common/app.helpers';
import { FormsAttributeService } from 'src/app/forms/forms-attribute.service';
import { FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { TableConstructorData } from 'src/app/interfaces/tables.interfaces';
import { PaginatableListOfBasicUsers, PaginatablePendingUserInvolvements, UserBasic } from 'src/app/swagger-api';
import { UserTableCreators } from 'src/app/views/table-users-list/adapters/table-creator.enum';

import { FieldAttributeConstructorService } from 'src/app/forms/fields/field-attributes-constructor.service';
import {
  SocialGroup,
  SocialGroupEntity,
  SocialGroupMembers,
  SocialGroupType,
} from '../../search/search-component/invite-to-social-group/social-group.interface';
import { SocialInitiativeShared } from './social-initiative-shared.abstract';

@Injectable({ providedIn: 'root' })
export abstract class SocialInitiative<T extends SocialGroupEntity> extends SocialInitiativeShared {
  protected membersChanges$: Subject<PaginatableListOfBasicUsers> = new Subject();

  protected formAttrChanges$: ReplaySubject<FormConstructorContent> = new ReplaySubject();
  protected membersTableDataChanges$: ReplaySubject<TableConstructorData> = new ReplaySubject();

  protected entity: T;
  protected members: UserBasic[] = [];
  protected leaders: UserBasic[] = [];

  protected router = inject(Router);
  protected sessionService = inject(SessionService);
  protected formsAttributeService = inject(FormsAttributeService);
  protected fieldsService = inject(FieldAttributeConstructorService);

  public abstract readonly SOCIAL_GROUP_TYPE: SocialGroupType;
  public abstract readonly SOCIAL_ENTITY_ROUTE_LINK: string;

  public abstract get entityChanged$(): Observable<T>;

  public abstract fetchEntityMemberIds(socialGroupId: string): Observable<SocialGroupMembers>;
  public abstract fetchEntityDetails(entityId: string): void;
  public abstract fetchEntityMembers(communityId: string, offset?: number, limit?: number): void;
  public abstract fetchUsersRequestsForEntity(
    entityId: string,
    offset: number,
    limit: number,
    sorting: string,
    allowManaging: boolean,
  ): void;

  public abstract fetchUsersInvitationsForEntity(
    entityId: string,
    offset: number,
    limit: number,
    sorting: string,
    allowManaging: boolean,
  ): void;

  public abstract fetchAttributeMetadata(data?: T, isManageData?: boolean): void;
  public abstract createEntity(entity: object): void;
  public abstract updateEntity(entity: object, entityId: string): void;
  public abstract joinEntity(entityId: string, offset: number, limit: number): void;
  public abstract inviteToEntity(userId: string, entityId: string): void;
  public abstract leaveEntity(entityId: string, offset: number, limit: number): void;
  public abstract removeMember(userId: string, entityId: string, offset: number, limit: number): void;
  public abstract navigateToInviteToEntitySearchPage(id: string): void;

  public get membersChanged(): Observable<PaginatableListOfBasicUsers> {
    return this.membersChanges$.asObservable();
  }

  public get onFormAttrChanges(): Observable<FormConstructorContent> {
    return this.formAttrChanges$.asObservable();
  }

  public get membersTableDataChanges(): Observable<TableConstructorData> {
    return this.membersTableDataChanges$.asObservable();
  }

  public get socialGroup$(): Observable<SocialGroup> {
    return this.entityChanged$.pipe(
      switchMap(({ entity_id, title, creator }: T) =>
        this.fetchEntityMemberIds(entity_id).pipe(
          map((members: SocialGroupMembers) => ({
            name: title,
            entityId: entity_id,
            membersIds: members,
            creatorId: creator.user_id,
            type: this.SOCIAL_GROUP_TYPE,
            entityLink: this.SOCIAL_ENTITY_ROUTE_LINK,
          })),
        ),
      ),
    );
  }

  public fetchEntityData(entityId: string): void {
    this.fetchEntityDetails(entityId);
  }

  protected completeSubject(): void {
    completeSubject(this.membersChanges$);
    completeSubject(this.formAttrChanges$);
    completeSubject(this.membersTableDataChanges$);
  }

  protected fetchEntityUsersRequests(
    api$: Observable<PaginatablePendingUserInvolvements>,
    entityId: string,
    allowManaging: boolean,
  ): void {
    api$.pipe(catchError(() => EMPTY)).subscribe((requests: PaginatablePendingUserInvolvements) => {
      this.userRequests = requests.items;
      this.notifyTableDataChanges(UserTableCreators.ENTITY_USERS_REQUESTS, {
        usersRequests: this.userRequests,
        entityId,
        allowManaging,
      });
      this.notifyResolverChanges(requests);
    });
  }

  protected fetchEntityInvites(
    api$: Observable<PaginatablePendingUserInvolvements>,
    entityId: string,
    allowManaging: boolean,
  ): void {
    api$.pipe(catchError(() => EMPTY)).subscribe((requests: PaginatablePendingUserInvolvements) => {
      this.invites = requests.items;

      this.notifyTableDataChanges(UserTableCreators.ENTITY_USERS_INVITES, {
        usersRequests: this.invites,
        entityId,
        allowManaging,
      });
      this.notifyResolverChanges(requests);
    });
  }

  protected navigateToInvite(link: string): void {
    promiseWithCatch(this.router.navigateByUrl(link));
  }
}
